import { PlusIcon } from "@heroicons/react/20/solid";
import {
  KeyIcon,
  TrashIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { DotMenu } from "components/DotMenu";
import { FC, useEffect, useState } from "react";
import {
  CompanyQueryKey,
  createCompanyApiToken,
  deleteCompanyApiToken,
} from "api/company.api";
import PrimaryButton from "components/buttons/PrimaryButton";
import Modal from "components/Modal";
import DangerModal from "components/DangerModal";
import { AddApiKeyModal, AddApiKeyFormData } from "./AddApiKeyModal";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "hooks/useNotification";
import { ModalContentWrapper } from "components/ModalContentWrapper";
import { Company } from "types/company.type";
import { ApiToken } from "types/apiToken.type";
import { Card, CardBody, CardHeader, EmptyState, MicroLabel } from "./ui";

interface Props {
  company: Company;
}

export const ApiTab: FC<Props> = ({ company }) => {
  const [isAddApiKeyModalOpen, setIsAddApiKeyModalOpen] = useState(false);
  const [createdApiToken, setCreatedApiToken] = useState<ApiToken | null>(null);
  const [tokenToDelete, setTokenToDelete] = useState<ApiToken | null>(null);
  const queryClient = useQueryClient();
  const notification = useNotification();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddApiKeyFormData>();

  const {
    mutate: createApiKey,
    isPending: isCreatingApiKey,
    isSuccess: isCreateApiKeySuccess,
  } = useMutation({
    ...createCompanyApiToken(),
    onSuccess: (data: ApiToken) => {
      setCreatedApiToken(data);
      notification.info("API-nyckel skapad");
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetOne, company.id],
      });
    },
  });

  const { mutate: deleteApiKey, isPending: isDeletingApiKey } = useMutation({
    ...deleteCompanyApiToken(),
    onSuccess: () => {
      notification.info("API-nyckel borttagen");
      setTokenToDelete(null);
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetOne, company.id],
      });
    },
  });

  const onSubmit = handleSubmit((data: AddApiKeyFormData) => {
    createApiKey({ companyId: company.id, name: data.name });
  });

  const confirmDeleteApiKey = () => {
    if (tokenToDelete) {
      deleteApiKey({ companyId: company.id, tokenId: tokenToDelete.id });
    }
  };

  useEffect(() => {
    document.title = "API";
  }, []);

  useEffect(() => {
    if (isCreateApiKeySuccess && !createdApiToken) {
      setIsAddApiKeyModalOpen(false);
    }
  }, [isCreateApiKeySuccess, createdApiToken]);

  const tokens = company.apiTokens ?? [];

  return (
    <Card>
      <CardHeader
        icon={KeyIcon}
        title="Driftjournalen API"
        description="Koppla ert affärssystem mot Driftjournalen för att skapa uppdrag och synka kunder."
        action={
          <PrimaryButton
            onClick={() => setIsAddApiKeyModalOpen(true)}
            size="small"
          >
            <PlusIcon className="mr-1.5 h-4 w-4" /> Ny API-nyckel
          </PrimaryButton>
        }
      />

      <CardBody>
        <MicroLabel>API-nycklar</MicroLabel>
        <div className="mt-3 space-y-2.5">
          {tokens.length > 0 ? (
            tokens.map((apiToken) => (
              <div
                key={apiToken.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-4"
              >
                <div className="flex min-w-0 items-center gap-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <KeyIcon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {apiToken.name}
                    </p>
                    <p className="mt-0.5 truncate font-mono text-xs text-slate-500">
                      {apiToken.token}
                    </p>
                  </div>
                </div>
                <DotMenu
                  items={[
                    {
                      label: "Ta bort nyckel",
                      onClick: () => setTokenToDelete(apiToken),
                      isDelete: true,
                    },
                  ]}
                />
              </div>
            ))
          ) : (
            <EmptyState
              icon={KeyIcon}
              title="Inga API-nycklar än"
              description="Skapa en nyckel för att börja integrera era system."
              action={
                <PrimaryButton
                  onClick={() => setIsAddApiKeyModalOpen(true)}
                  size="small"
                >
                  <PlusIcon className="mr-1.5 h-4 w-4" /> Ny API-nyckel
                </PrimaryButton>
              }
            />
          )}
        </div>
      </CardBody>

      <Modal
        isOpen={isAddApiKeyModalOpen}
        onClose={() => {
          setIsAddApiKeyModalOpen(false);
        }}
        afterClose={() => {
          setCreatedApiToken(null);
          reset({ name: "" });
        }}
        title="Skapa API-nyckel"
        actionText="Skapa"
        isPrettyWide
        actionHandler={createdApiToken ? undefined : onSubmit}
        isLoading={isCreatingApiKey}
        loadingText="Skapar..."
        closeButtonText={createdApiToken ? "Stäng" : "Avbryt"}
      >
        <ModalContentWrapper className="pb-8">
          {!createdApiToken ? (
            <AddApiKeyModal register={register} errors={errors} />
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-800">
                  <strong>Viktigt:</strong> API-nyckeln visas endast en gång.
                  Spara den på ett säkert ställe.
                </p>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Din API-nyckel
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-900">
                    {createdApiToken.token}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(createdApiToken.token);
                      notification.info("API-nyckel kopierad");
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Kopiera
                  </button>
                </div>
              </div>
            </div>
          )}
        </ModalContentWrapper>
      </Modal>

      <DangerModal
        isOpen={!!tokenToDelete}
        onClose={() => setTokenToDelete(null)}
        title="Ta bort API-nyckel"
        paragraphs={[
          `Är du säker på att du vill ta bort API-nyckeln "${tokenToDelete?.name}"?`,
          "Detta går inte att ångra. Externa system som använder nyckeln kommer behöva en ny för att fortsätta kommunicera med Driftjournalen.",
        ]}
        actionText="Ta bort"
        actionHandler={confirmDeleteApiKey}
        isLoading={isDeletingApiKey}
        loadingText="Tar bort..."
      />
    </Card>
  );
};
